/**
 * Flat vector pieces in the style of the brand illustrations. Kept quiet on
 * purpose: they sit behind type and photography, never compete with it.
 */

/** The canonical silhouette: a rounded square with one sharp corner, bottom left. */
const TILE = 'M32 6 H88 A26 26 0 0 1 114 32 V88 A26 26 0 0 1 88 114 H6 V32 A26 26 0 0 1 32 6 Z'

/**
 * The mark, in the theme's own two tones: the silhouette always contrasts the
 * background, the six dots are always the colour of the background, and the
 * one accent dot never changes.
 */
export function BrandMark({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label="AIroundMe">
      <path d={TILE} fill="var(--brand-ink-strong)" />
      <circle cx="36" cy="40" r="5.5" fill="var(--brand-canvas)" />
      <circle cx="62" cy="30" r="4.5" fill="var(--brand-canvas)" />
      <circle cx="86" cy="44" r="6" fill="var(--brand-canvas)" />
      <circle cx="88" cy="74" r="5" fill="var(--brand-canvas)" />
      <circle cx="64" cy="90" r="5.5" fill="var(--brand-canvas)" />
      <circle cx="36" cy="80" r="5" fill="var(--brand-canvas)" />
      <circle cx="61" cy="60" r="11" fill="var(--brand-accent)" />
    </svg>
  )
}

/**
 * The avatar variant: no silhouette at all. A round crop would cut the sharp
 * corner off, so the circle is drawn on purpose, and the dots grow half a unit
 * to make up for the extra air around them.
 */
export function BrandAvatar({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label="AIroundMe">
      <circle cx="60" cy="60" r="60" fill="var(--brand-ink-strong)" />
      <circle cx="36" cy="40" r="6" fill="var(--brand-canvas)" />
      <circle cx="62" cy="30" r="5" fill="var(--brand-canvas)" />
      <circle cx="86" cy="44" r="6.5" fill="var(--brand-canvas)" />
      <circle cx="88" cy="74" r="5.5" fill="var(--brand-canvas)" />
      <circle cx="64" cy="90" r="6" fill="var(--brand-canvas)" />
      <circle cx="36" cy="80" r="5.5" fill="var(--brand-canvas)" />
      <circle cx="61" cy="60" r="11" fill="var(--brand-accent)" />
    </svg>
  )
}

/**
 * The mascot is the mark in another state, not a second character: the
 * silhouette becomes a head, two of the dots line up as eyes, and the accent
 * dot steps outside the head, where it means "what it found" instead of "you".
 * No mouth, no brows, no hands — the expression is the eyes and nothing else.
 */
export function Mascot({
  state = 'idle',
  className = 'h-28 w-32',
}: {
  state?: 'idle' | 'searching' | 'found'
  className?: string
}) {
  const eyes =
    state === 'found'
      ? { left: 52, right: 82, cy: 60, r: 11.5 }
      : state === 'searching'
        ? { left: 56, right: 84, cy: 62, r: 9.5 }
        : { left: 50, right: 78, cy: 62, r: 9.5 }

  return (
    <svg viewBox="0 0 150 130" className={className} aria-hidden>
      <g transform="translate(8 12) scale(0.92)">
        <path d={TILE} fill="var(--brand-ink-strong)" />
      </g>
      <circle cx={eyes.left} cy={eyes.cy} r={eyes.r} fill="var(--brand-canvas)" />
      <circle cx={eyes.right} cy={eyes.cy} r={eyes.r} fill="var(--brand-canvas)" />
      {state === 'searching' && (
        <>
          <circle cx="128" cy="34" r="5.5" fill="var(--brand-ink-strong)" />
          <circle cx="138" cy="66" r="4.5" fill="var(--brand-ink-strong)" />
          <circle cx="124" cy="96" r="5" fill="var(--brand-ink-strong)" />
        </>
      )}
      {state === 'found' && <circle cx="128" cy="36" r="13" fill="var(--brand-accent)" />}
    </svg>
  )
}

/** The city line from the reference: a signature strip, not a picture. */
export function CitySkyline({ className = 'h-12 w-full', onDark }: { className?: string; onDark?: boolean }) {
  const solid = onDark ? 'rgba(255,255,255,0.22)' : 'var(--brand-ink)'
  const light = onDark ? 'var(--brand-sand)' : 'var(--brand-sand)'

  return (
    <svg viewBox="0 0 320 48" className={className} preserveAspectRatio="none" aria-hidden>
      <rect x="6" y="20" width="26" height="28" rx="3" fill={light} opacity={onDark ? 0.55 : 1} />
      <rect x="38" y="8" width="16" height="40" rx="4" fill={solid} />
      <path d="M46 0l4 9h-8z" fill={light} opacity={onDark ? 0.75 : 1} />
      <rect x="60" y="26" width="34" height="22" rx="3" fill={solid} />
      <rect x="100" y="14" width="22" height="34" rx="10" fill={light} opacity={onDark ? 0.45 : 1} />
      <rect x="128" y="30" width="40" height="18" rx="3" fill={solid} />
      <rect x="174" y="18" width="18" height="30" rx="4" fill={light} opacity={onDark ? 0.6 : 1} />
      <circle cx="183" cy="14" r="7" fill={light} opacity={onDark ? 0.8 : 1} />
      <rect x="198" y="28" width="30" height="20" rx="3" fill={solid} />
      <rect x="234" y="12" width="20" height="36" rx="5" fill={light} opacity={onDark ? 0.4 : 1} />
      <rect x="260" y="24" width="28" height="24" rx="3" fill={solid} />
      <rect x="294" y="32" width="20" height="16" rx="3" fill={light} opacity={onDark ? 0.5 : 1} />
    </svg>
  )
}

/** Stands in for a missing photo: never an empty grey box. */
export function PlaceGlyph({ className = 'h-full w-full', seed = 0 }: { className?: string; seed?: number }) {
  const variant = Math.abs(seed) % 3
  const back = 'var(--brand-card-sunk)'
  const near = 'var(--brand-ink-faint)'
  const far = 'var(--brand-hairline-strong)'

  return (
    <svg viewBox="0 0 120 80" className={className} preserveAspectRatio="xMidYMax slice" aria-hidden>
      <rect width="120" height="80" fill={back} />
      {variant === 0 && (
        <>
          <rect x="16" y="42" width="30" height="38" rx="2" fill={far} />
          <rect x="54" y="26" width="15" height="54" rx="3" fill={near} opacity="0.55" />
          <path d="M61.5 16l3.5 10h-7z" fill={near} opacity="0.55" />
          <rect x="77" y="48" width="27" height="32" rx="2" fill={far} />
        </>
      )}
      {variant === 1 && (
        <>
          <rect x="24" y="50" width="72" height="30" rx="2" fill={far} />
          <rect x="52" y="24" width="17" height="28" rx="8.5" fill={near} opacity="0.55" />
          <circle cx="60.5" cy="20" r="6.5" fill={near} opacity="0.55" />
          <rect x="31" y="58" width="7" height="22" rx="1.5" fill={near} opacity="0.35" />
          <rect x="82" y="58" width="7" height="22" rx="1.5" fill={near} opacity="0.35" />
        </>
      )}
      {variant === 2 && (
        <>
          <rect x="14" y="54" width="92" height="26" rx="2" fill={far} />
          <path d="M60 26l34 28H26z" fill={near} opacity="0.5" />
          <rect x="53" y="64" width="14" height="16" rx="1.5" fill={near} opacity="0.35" />
        </>
      )}
    </svg>
  )
}

/**
 * The wait on a search: the mascot in its searching state. The head never
 * moves — only the eyes scan, the three dark dots outside arrive one by one,
 * and the last of them turns into the orange find at the end of the cycle.
 */
export function SearchingScene({ className = 'h-28 w-32' }: { className?: string }) {
  return (
    <svg viewBox="0 0 150 130" className={className} aria-hidden>
      <g transform="translate(8 12) scale(0.92)">
        <path d={TILE} fill="var(--brand-ink-strong)" />
      </g>

      <g className="mascot-scan">
        <circle cx="56" cy="62" r="9.5" fill="var(--brand-canvas)" />
        <circle cx="84" cy="62" r="9.5" fill="var(--brand-canvas)" />
      </g>

      <circle className="mascot-probe" cx="128" cy="34" r="5.5" fill="var(--brand-ink-strong)" />
      <circle
        className="mascot-probe"
        cx="138"
        cy="66"
        r="4.5"
        fill="var(--brand-ink-strong)"
        style={{ animationDelay: '260ms' }}
      />
      <circle
        className="mascot-probe"
        cx="124"
        cy="96"
        r="5"
        fill="var(--brand-ink-strong)"
        style={{ animationDelay: '520ms' }}
      />

      {/* The find: the accent dot, outside the head, at the end of every pass */}
      <circle className="mascot-find" cx="128" cy="36" r="13" fill="var(--brand-accent)" />
    </svg>
  )
}

/** Empty results, refused geolocation: the mascot simply waiting. */
export function EmptyIllustration({ className = 'h-24 w-28' }: { className?: string }) {
  return <Mascot state="idle" className={className} />
}
